import { useMemo, useState, useEffect } from 'react';

type TimezoneValue = { name: string; offset: number };

interface TimezoneSelectorProps {
  value: TimezoneValue;
  onChange: (tz: TimezoneValue) => void;
}

const popularTimezones: Array<{ name: string; label: string }> = [
  { name: 'America/Tijuana', label: 'Tijuana (Baja California)' },
  { name: 'America/Denver', label: 'Denver (Montaña)' },
  { name: 'America/Chicago', label: 'Chicago (Centro)' },
  { name: 'America/Mexico_City', label: 'Ciudad de México' },
  { name: 'America/Guatemala', label: 'Guatemala' },
  { name: 'America/Bogota', label: 'Bogotá' },
  { name: 'America/Lima', label: 'Lima' },
  { name: 'America/Caracas', label: 'Caracas' },
  { name: 'America/Santo_Domingo', label: 'Santo Domingo' },
  { name: 'America/Santiago', label: 'Santiago' },
  { name: 'America/La_Paz', label: 'La Paz' },
  { name: 'America/Asuncion', label: 'Asunción' },
  { name: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires' },
  { name: 'America/Sao_Paulo', label: 'São Paulo' },
  { name: 'America/New_York', label: 'Nueva York' },
  { name: 'Europe/Madrid', label: 'Madrid' },
  { name: 'Europe/London', label: 'Londres' },
];

const getOffsetForZone = (timeZone: string): number => {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    const parts = formatter.formatToParts(now);
    const tzDate = new Date(
      `${parts.find(p => p.type === 'year')?.value}-${parts.find(p => p.type === 'month')?.value}-${parts.find(p => p.type === 'day')?.value}T${parts.find(p => p.type === 'hour')?.value}:${parts.find(p => p.type === 'minute')?.value}:${parts.find(p => p.type === 'second')?.value}.000Z`
    );
    const diffMs = tzDate.getTime() - now.getTime();
    const diffHours = Math.round(diffMs / (1000 * 60 * 60));
    return diffHours;
  } catch {
    return -new Date().getTimezoneOffset() / 60;
  }
};

const formatGMT = (offset: number) => {
  const sign = offset >= 0 ? '+' : '-';
  const abs = Math.abs(offset);
  const hours = Math.floor(abs);
  const minutes = (abs - hours) * 60;
  const mm = minutes ? `:${String(minutes).padStart(2, '0')}` : '';
  return `GMT${sign}${String(hours).padStart(2, '0')}${mm}`;
};

export default function TimezoneSelector({ value, onChange }: TimezoneSelectorProps) {
  const detectedZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Mexico_City', []);
  const [query, setQuery] = useState('');
  const [zones, setZones] = useState<Array<{ name: string; label: string }>>(popularTimezones);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  useEffect(() => {
    // Si el navegador soporta la lista completa de zonas, úsala con labels amigables
    const anyIntl: any = Intl as any;
    if (typeof anyIntl.supportedValuesOf === 'function') {
      const all = (anyIntl.supportedValuesOf('timeZone') as string[])
        .filter(z => z.includes('/') && !z.startsWith('Etc/'))
        .map(z => ({ name: z, label: z.replace(/_/g, ' ') }));
      setZones(all);
    }
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return zones;
    return zones.filter(z => z.label.toLowerCase().includes(q) || z.name.toLowerCase().includes(q));
  }, [query, zones]);

  const handleSelect = (tzName: string) => {
    const offset = getOffsetForZone(tzName);
    onChange({ name: tzName, offset });
    setQuery('');
    setIsPickerOpen(false);
  };

  const currentZone = value?.name || detectedZone;
  const currentOffset = value?.offset ?? getOffsetForZone(value?.name || detectedZone);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <div className="text-sm text-[#85929e]">Actual: {currentZone.replace(/_/g, ' ')}</div>
          <div className="text-xs text-[#2C4358] font-semibold">{formatGMT(currentOffset)}</div>
        </div>
        <button
          type="button"
          onClick={() => setIsPickerOpen(v => !v)}
          className="text-sm font-semibold text-[#22B6B2] underline underline-offset-2 hover:opacity-80"
        >
          {isPickerOpen ? 'Ocultar' : 'Cambiar'}
        </button>
      </div>

      {isPickerOpen && (
        <>
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => handleSelect(detectedZone)}
              className="px-3 py-2 text-sm rounded-xl bg-[#f7ebd6] text-[#2C4358] border border-[#f7ebd6]/50 hover:bg-[#f0e0c0] transition-colors"
              title="Usar zona horaria detectada automáticamente"
            >
              Usar detectada
            </button>
            <div className="text-xs text-[#85929e]">
              Detectada: <span className="font-semibold text-[#2C4358]">{detectedZone.replace(/_/g, ' ')}</span>
            </div>
          </div>

          <div className="relative">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar ciudad o zona (ej. Ciudad de México, Bogotá)"
              className="w-full px-4 py-3 bg-white border-2 border-[#22B6B2]/30 rounded-2xl text-base text-[#2C4358] placeholder-[#85929e] focus:outline-none focus:border-[#22B6B2] transition"
            />
          </div>

          <div className="max-h-56 overflow-auto rounded-2xl border border-[#22B6B2]/20 divide-y divide-gray-100 bg-white">
            {filtered.slice(0, 100).map((z) => {
              const off = getOffsetForZone(z.name);
              return (
                <button
                  key={z.name}
                  type="button"
                  onClick={() => handleSelect(z.name)}
                  className={`w-full text-left px-4 py-3 hover:bg-[#22B6B2]/5 transition flex items-center justify-between ${currentZone === z.name ? 'bg-[#22B6B2]/10' : ''}`}
                >
                  <span className="text-[#2C4358]">{z.label}</span>
                  <span className="text-xs text-[#85929e] font-semibold ml-3">{formatGMT(off)}</span>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="px-4 py-6 text-center text-[#85929e]">Sin resultados. Intenta con otra ciudad.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}


